import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from './config';
import { FUNCTIONS_REGION } from './region';
import type { CurriculumUnit } from '../quran/curriculumProgress';

export type Curriculum = {
  id: string;
  name: string;
  country: string;
  grade: number;
  term: 1 | 2 | 3;
  units: CurriculumUnit[];
  totalAyahs: number;
  published: boolean;
};

/** Where a student sits in school. Both null until they have said. */
export type Schooling = {
  country: string | null;
  grade: number | null;
  loading: boolean;
};

const toCurriculum = (id: string, data: Record<string, unknown>): Curriculum => ({
  id,
  name: String(data.name ?? ''),
  country: String(data.country ?? ''),
  grade: Number(data.grade ?? 0),
  term: (data.term === 2 || data.term === 3 ? data.term : 1) as 1 | 2 | 3,
  // `from`/`to` are stored as null for a whole surah; the progress helpers
  // read them as absent, so the null is dropped rather than passed on.
  units: (Array.isArray(data.units) ? data.units : []).map((u: Record<string, unknown>) => (
    u && u.from !== null && u.from !== undefined
      ? { surah: Number(u.surah), from: Number(u.from), to: Number(u.to) }
      : { surah: Number(u.surah) }
  )),
  totalAyahs: Number(data.totalAyahs ?? 0),
  published: data.published === true,
});

/** Every curriculum, for the admin who edits them — drafts included. */
export function listenToCurricula(
  next: (list: Curriculum[]) => void,
  onError?: () => void
): () => void {
  return onSnapshot(
    collection(db, 'curricula'),
    (snap) => next(
      snap.docs
        .map((d) => toCurriculum(d.id, d.data()))
        // Country, then grade, then term: the order an admin builds them in.
        .sort((a, b) => a.country.localeCompare(b.country) || a.grade - b.grade || a.term - b.term)
    ),
    () => onError?.()
  );
}

/**
 * The student's own schooling, read from their user document.
 *
 * Separate from `useIdentity`, which carries the name and nothing else, and
 * separate from the profile hook so a page that only needs the grade does not
 * re-render on every profile field.
 */
export function useSchooling(uid: string | null): Schooling {
  const [state, setState] = useState<Schooling>({ country: null, grade: null, loading: true });
  useEffect(() => {
    if (!uid) { setState({ country: null, grade: null, loading: false }); return undefined; }
    setState({ country: null, grade: null, loading: true });
    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const data = snap.data() ?? {};
        setState({
          country: typeof data.country === 'string' && data.country ? data.country : null,
          grade: Number.isInteger(data.grade) ? (data.grade as number) : null,
          loading: false,
        });
      },
      () => setState({ country: null, grade: null, loading: false })
    );
  }, [uid]);
  return state;
}

/**
 * The curricula a student is measured against — DERIVED from their grade, not
 * held in an enrolment table.
 *
 * There is no join document and nothing to keep in step: a student's syllabus
 * is whatever is published for their country and grade, so changing their
 * grade changes their syllabus in the same instant and cannot leave them
 * enrolled in last year's. That is also why the query filters on `published` —
 * a term an admin is still entering must not become what a class is measured
 * against.
 */
export function useMyCurricula(
  country: string | null,
  grade: number | null
): { curricula: Curriculum[]; loading: boolean } {
  const [state, setState] = useState<{ curricula: Curriculum[]; loading: boolean }>(
    { curricula: [], loading: true }
  );
  useEffect(() => {
    if (!country || !grade) { setState({ curricula: [], loading: false }); return undefined; }
    setState({ curricula: [], loading: true });
    const q = query(
      collection(db, 'curricula'),
      where('country', '==', country),
      where('grade', '==', grade),
      where('published', '==', true)
    );
    return onSnapshot(
      q,
      (snap) => setState({
        curricula: snap.docs
          .map((d) => toCurriculum(d.id, d.data()))
          .sort((a, b) => a.term - b.term),
        loading: false,
      }),
      () => setState({ curricula: [], loading: false })
    );
  }, [country, grade]);
  return state;
}

const fns = () => getFunctions(app, FUNCTIONS_REGION);

export async function saveCurriculum(input: {
  name: string;
  country: string;
  grade: number;
  term: number;
  units: CurriculumUnit[];
  published: boolean;
}): Promise<string> {
  const call = httpsCallable<typeof input, { id: string }>(fns(), 'saveCurriculum');
  const out = await call(input);
  return out.data.id;
}
